// React
import { FunctionComponent, useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
// Components
import JanusPage from "../../components/JanusPage/JanusPage";
import QuestionnaireService from "../../services/QuestionnaireService";
// Resources
import { Questionnaire, QuestionnaireResponse } from "fhir/r5";
// Translation
import i18n from "i18next";
// FHIR
import Client from "fhir-kit-client";
// HL7-Front-Library
import { QuestionnaireDisplay, ValueSetLoader } from "@fyrstain/hl7-front-library";

const QuestionnaireResponseFiller: FunctionComponent = () => {
  /////////////////////////////////////
  //      Constants / ValueSet       //
  /////////////////////////////////////

  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  // Questionnaire constants
  const { questionnaireId } = useParams();
  const [questionnaireResource, setQuestionnaireResource] = useState({} as Questionnaire);
  const [questionnaireResponseResource, setQuestionnaireResponseResource] = useState({} as QuestionnaireResponse);

  // An alert to display success or error message
  const [alert, setAlert] = useState<{
    message: string;
    isError: boolean;
  } | null>(null);

  /////////////////////////////////////
  //             Client              //
  /////////////////////////////////////

  const fhirClient = new Client({
    baseUrl: process.env.REACT_APP_FHIR_URL ?? "fhir",
  });

  //////////////////////////////
  //           Error          //
  //////////////////////////////

  /**
   * Navigate to the Error page
   */
  const onError = useCallback(
    (error?: unknown) => {
      navigate("/Error", { state: { error } });
    },
    [navigate],
  );

  ////////////////////////////////
  //           Actions          //
  ////////////////////////////////

  /**
   * To load the Questionnaire and use the $populate operation.
   */
  const load = useCallback(async () => {
    try {
      setLoading(true);
      const questionnaire = await QuestionnaireService.loadQuestionnaire(questionnaireId as string);
      setQuestionnaireResource(questionnaire);
      const questionnaireResponse = await QuestionnaireService.populate(questionnaire, "123");
      setQuestionnaireResponseResource(questionnaireResponse);
    } catch (error) {
      onError();
      setLoading(false);
    } finally {
      setLoading(false);
    }
  }, [onError, questionnaireId]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * To handle the submit of the QuestionnaireResponse.
   * @param response The QuestionnaireResponse to submit.
   */
  const handleSubmit = (response: QuestionnaireResponse) => {
    setQuestionnaireResponseResource(response);
    response.subject = undefined;
    fhirClient
      .create({ body: response, resourceType: "QuestionnaireResponse" })
      .then(() => {
        setAlert({
          message: "text.successsubmitform",
          isError: false,
        });
        setTimeout(() => {
          navigate("/Home");
        }, 3000);
      })
      .catch(() => {
        setAlert({
          message: "text.errorsubmitform",
          isError: true,
        });
      });
  };

  //////////////////////////////
  //          Content         //
  //////////////////////////////

  return (
    <JanusPage titleKey="title.questionnaireresponseform" loading={loading} fitFooter={true} needsLogin={false}>
      <>
        <QuestionnaireDisplay
          language={i18n.t}
          questionnaire={questionnaireResource}
          questionnaireResponse={questionnaireResponseResource}
          valueSetLoader={new ValueSetLoader(fhirClient)}
          cqlEvaluator={QuestionnaireService.cqlEvaluator}
          onSubmit={handleSubmit}
        />
        {alert && (
          <div className={`mt-3 alert ${alert.isError ? "alert-danger" : "alert-success"}`} role="alert">
            {i18n.t(alert.message)}
          </div>
        )}
      </>
    </JanusPage>
  );
};

export default QuestionnaireResponseFiller;
